import React, { useEffect, useState } from 'react';
import { AlertTriangle, ExternalLink, FileText, RefreshCw } from 'lucide-react';
import { listNFes, NFListResponse, runSefazSync, SefazSyncResult } from '../services/nfseService';

function formatBRL(value: number | null): string {
  if (value == null) return '—';
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export default function NotasFiscaisPage() {
  const [data, setData] = useState<NFListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<SefazSyncResult | null>(null);

  const reload = () => {
    setLoading(true);
    listNFes()
      .then(setData)
      .catch(() => setError('Erro ao carregar notas fiscais'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { reload(); }, []);

  const handleSync = async (forceFromZero: boolean) => {
    setSyncing(true);
    setError('');
    setSyncResult(null);
    try {
      const result = await runSefazSync(forceFromZero);
      setSyncResult(result);
      if (result.docs_new > 0) reload();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao sincronizar com SEFAZ');
    } finally {
      setSyncing(false);
    }
  };

  if (loading) return <div className="p-6 text-gray-500">Carregando…</div>;
  if (error) return <div className="p-6 text-red-600">{error}</div>;
  if (!data) return null;

  // Build merged rows: NF entries + gap markers, ordered by número
  type Row = { kind: 'nf'; entry: NFListResponse['nfes'][number] } | { kind: 'gap'; numero: number };
  const rows: Row[] = [];
  let cursor = data.first_numero ?? 0;
  const byNum = new Map<number, NFListResponse['nfes'][number]>();
  const noNumber: NFListResponse['nfes'] = [];
  for (const nf of data.nfes) {
    if (nf.numero_int != null) byNum.set(nf.numero_int, nf);
    else noNumber.push(nf);
  }
  if (data.first_numero != null && data.last_numero != null) {
    for (let n = data.first_numero; n <= data.last_numero; n++) {
      const nf = byNum.get(n);
      if (nf) rows.push({ kind: 'nf', entry: nf });
      else rows.push({ kind: 'gap', numero: n });
      cursor = n;
    }
  }
  // Entries without numero_int — append at the end
  for (const nf of noNumber) rows.push({ kind: 'nf', entry: nf });

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FileText className="h-6 w-6 text-blue-600" />
            Notas Fiscais Emitidas
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {data.total} NF{data.total === 1 ? '' : 's'} no journal
            {data.first_numero != null && data.last_numero != null && (
              <span className="ml-1">— sequência {data.first_numero} → {data.last_numero}</span>
            )}
          </p>
        </div>
        <div className="flex items-start gap-3">
          {data.gaps.length > 0 && (
            <div className="px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900 flex items-start gap-2 max-w-md">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>
                <strong>{data.gaps.length} gap{data.gaps.length === 1 ? '' : 's'}</strong> na sequência:
                {' '}
                {data.gaps.join(', ')}
              </span>
            </div>
          )}
          <div className="flex flex-col gap-2">
            <button
              onClick={() => handleSync(false)}
              disabled={syncing}
              className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1.5"
              title="Sincroniza incrementalmente a partir do último cursor NSU"
            >
              <RefreshCw className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Sincronizando…' : 'Sincronizar SEFAZ'}
            </button>
            {data.gaps.length > 0 && (
              <button
                onClick={() => handleSync(true)}
                disabled={syncing}
                className="px-3 py-1.5 text-xs text-blue-700 hover:text-blue-900 disabled:opacity-50"
                title="Re-baixar tudo desde NSU=0 (usar quando há gaps suspeitos)"
              >
                Sync completo (NSU=0)
              </button>
            )}
          </div>
        </div>
      </div>

      {syncResult && (
        <div className={`px-4 py-3 rounded-lg border text-sm ${
          syncResult.status === 'ok'
            ? 'bg-green-50 border-green-200 text-green-900'
            : 'bg-red-50 border-red-200 text-red-900'
        }`}>
          <div className="font-medium">
            Sync {syncResult.status}: {syncResult.docs_new} nova{syncResult.docs_new === 1 ? '' : 's'},
            {' '}{syncResult.docs_skipped} já existente{syncResult.docs_skipped === 1 ? '' : 's'},
            {' '}{syncResult.docs_found} total
          </div>
          <div className="text-xs mt-1 opacity-80">
            NSU {syncResult.cursor_before} → {syncResult.cursor_after}
          </div>
          {syncResult.errors.length > 0 && (
            <ul className="mt-2 text-xs list-disc list-inside">
              {syncResult.errors.slice(0, 5).map((e, i) => <li key={i}>{e}</li>)}
              {syncResult.errors.length > 5 && <li>... e mais {syncResult.errors.length - 5} erro(s)</li>}
            </ul>
          )}
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs text-gray-600 uppercase">
            <tr>
              <th className="px-4 py-3 text-left w-16">NF</th>
              <th className="px-4 py-3 text-left">Tomador</th>
              <th className="px-4 py-3 text-left w-28">Competência</th>
              <th className="px-4 py-3 text-left w-28">Emissão</th>
              <th className="px-4 py-3 text-right w-32">Valor</th>
              <th className="px-4 py-3 text-left w-24">ISS retido</th>
              <th className="px-4 py-3 text-left w-32">Município</th>
              <th className="px-4 py-3 text-left w-16">XML</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400">
                Nenhuma NFS-e encontrada no journal. Importe um XML em /importar.
              </td></tr>
            )}
            {rows.map((row, i) => {
              if (row.kind === 'gap') {
                return (
                  <tr key={`gap-${row.numero}`} className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold text-amber-900">{row.numero}</td>
                    <td className="px-4 py-2 text-amber-900" colSpan={7}>
                      <AlertTriangle className="inline h-4 w-4 mr-1 -mt-0.5" />
                      Faltando — número queimado ou NF não importada
                    </td>
                  </tr>
                );
              }
              const nf = row.entry;
              const cancelled = nf.status === 'cancelada';
              const pending = nf.status === 'pendente';
              return (
                <tr key={`nf-${nf.txn_id || i}`} className={`hover:bg-gray-50 ${cancelled ? 'bg-red-50/40 text-gray-500 line-through' : ''}`}>
                  <td className={`px-4 py-2 font-mono font-medium ${cancelled ? 'text-red-700' : 'text-gray-900'}`}>
                    {nf.numero}
                    {cancelled && <div className="text-[10px] font-sans no-underline text-red-700 font-semibold">CANCELADA</div>}
                    {pending && <div className="text-[10px] font-sans text-amber-700 font-semibold">PENDENTE</div>}
                  </td>
                  <td className="px-4 py-2">
                    <div className="truncate max-w-xs" title={nf.tomador_nome}>
                      {nf.tomador_nome || <span className="text-gray-400">—</span>}
                    </div>
                    {nf.tomador_cnpj && (
                      <div className="text-xs text-gray-500 font-mono no-underline">{nf.tomador_cnpj}</div>
                    )}
                    {cancelled && nf.cancela_motivo && (
                      <div className="text-xs text-red-700 no-underline mt-1" title={`em ${nf.cancelada_em}`}>
                        motivo: {nf.cancela_motivo}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2 text-gray-700 font-semibold" title={nf.data_competencia ? `dCompet: ${nf.data_competencia}` : ''}>
                    {nf.data_competencia
                      ? nf.data_competencia.split('-').reverse().join('/')
                      : (nf.competencia || '—')}
                  </td>
                  <td className="px-4 py-2 text-gray-700">
                    {nf.emitida_em ? nf.emitida_em.slice(0, 10) : nf.date.replace(/\//g, '-')}
                  </td>
                  <td className="px-4 py-2 text-right font-mono text-gray-900">
                    {formatBRL(nf.valor_bruto ?? nf.valor_liquido)}
                  </td>
                  <td className="px-4 py-2">
                    {nf.iss_retido === 'sim' ? (
                      <span className="px-2 py-0.5 rounded text-xs bg-amber-100 text-amber-800 font-medium">sim</span>
                    ) : nf.iss_retido === 'nao' ? (
                      <span className="text-gray-500 text-xs">não</span>
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-gray-700 text-xs">{nf.municipio_incidencia || '—'}</td>
                  <td className="px-4 py-2">
                    {nf.doc_key ? (
                      <a
                        href={`/api/documents/${encodeURI(nf.doc_key)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800"
                        title="Abrir XML"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
